/**
 * Drawn artwork for the marketing page.
 *
 * Everything here is vector on purpose. The page ships no photography, so
 * there is nothing to license, nothing to art-direct per release, and no
 * hero image that arrives after the text has already painted.
 */

/**
 * A dog, three-quarter view. Used at two sizes: small inside the phone
 * mockup's photo frame, and large leaning into the closing banner.
 */
export function DogPortrait({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 190" aria-hidden className={className}>
      <defs>
        <linearGradient id="dog-coat" x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0%" stopColor="#e79a4e" />
          <stop offset="100%" stopColor="#c9762f" />
        </linearGradient>
        <linearGradient id="dog-ear" x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0%" stopColor="#d0813a" />
          <stop offset="100%" stopColor="#a75f24" />
        </linearGradient>
      </defs>

      {/* Chest, behind the head so the head reads as in front of it. */}
      <path d="M52 152c0-22 21-36 48-36s48 14 48 36v38H52Z" fill="url(#dog-coat)" />
      <path d="M100 128c14 0 25 10 27 24 1.6 11-6 22-27 22s-28.6-11-27-22c2-14 13-24 27-24Z" fill="#fdf6ec" />

      {/* Ears. */}
      <path d="M46 44c-6 16-5 40 3 56 6 12 15 8 17-4 2-14-2-40-8-52-3-6-9-6-12 0Z" fill="url(#dog-ear)" />
      <path d="M154 44c6 16 5 40-3 56-6 12-15 8-17-4-2-14 2-40 8-52 3-6 9-6 12 0Z" fill="url(#dog-ear)" />

      {/* Head. */}
      <path d="M100 20c30 0 48 22 48 54 0 30-20 52-48 52s-48-22-48-52c0-32 18-54 48-54Z" fill="url(#dog-coat)" />

      {/* Muzzle. */}
      <path d="M100 68c17 0 27 12 27 27s-12 24-27 24-27-9-27-24 10-27 27-27Z" fill="#fdf6ec" />
      <path d="M100 61c14 0 21 8 21 14 0 5-9 3-21 3s-21 2-21-3c0-6 7-14 21-14Z" fill="#fdf6ec" opacity="0.9" />

      {/* Eyes, brows, nose, mouth. */}
      <ellipse cx="80" cy="66" rx="7" ry="8" fill="#2b1d12" />
      <ellipse cx="120" cy="66" rx="7" ry="8" fill="#2b1d12" />
      <circle cx="82.4" cy="63" r="2.4" fill="#fff" />
      <circle cx="122.4" cy="63" r="2.4" fill="#fff" />
      <path d="M70 52c4-3 9-3.4 13-1.6M130 52c-4-3-9-3.4-13-1.6" stroke="#8a5322" strokeWidth="3.4" strokeLinecap="round" fill="none" />
      <path d="M100 82c6.6 0 11 3 11 6.4 0 3.6-5 7-11 7s-11-3.4-11-7c0-3.4 4.4-6.4 11-6.4Z" fill="#2b1d12" />
      <path d="M100 95v7" stroke="#2b1d12" strokeWidth="3" strokeLinecap="round" />
      <path d="M100 102c0 5-4.4 8-9 8s-8-3-8-6.6M100 102c0 5 4.4 8 9 8s8-3 8-6.6" stroke="#2b1d12" strokeWidth="3" strokeLinecap="round" fill="none" />

      {/* Tongue. Small, and the reason the whole face reads as pleased. */}
      <path d="M92 110c0-3 3.6-5 8-5s8 2 8 5v7c0 4.4-3.6 7-8 7s-8-2.6-8-7Z" fill="#e2707f" />

      {/* Collar. */}
      <path d="M68 126c9 7 20 10 32 10s23-3 32-10l4 11c-10 8-22 12-36 12s-26-4-36-12Z" fill="#6d47e8" />
      <circle cx="100" cy="149" r="7" fill="#f3c34a" />
    </svg>
  );
}

/**
 * The five faces above "trusted by". Silhouettes rather than stock portraits:
 * a made-up customer count next to a real stranger's photograph is the part of
 * a landing page that is actually dishonest.
 */
export function AvatarRow({ className }: { className?: string }) {
  const tints = ['#c9b6f7', '#f6c9a8', '#a8d5c2', '#f3b9c4', '#b6cdf4'];
  return (
    <div className={`flex ${className ?? ''}`}>
      {tints.map((tint, index) => (
        <svg
          key={tint}
          viewBox="0 0 40 40"
          aria-hidden
          className="h-9 w-9 rounded-full ring-2 ring-white"
          style={{ marginLeft: index === 0 ? 0 : '-0.6rem', background: tint }}
        >
          <circle cx="20" cy="15" r="6.6" fill="rgba(23,20,42,0.45)" />
          <path d="M6.6 40c0-7.4 6-13.4 13.4-13.4S33.4 32.6 33.4 40Z" fill="rgba(23,20,42,0.45)" />
        </svg>
      ))}
    </div>
  );
}

/** The scribbled excitement marks either side of the banner dog. */
export function Sparks({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 40 40" fill="none" aria-hidden className={className}>
      <path
        d="M6 20H1M8 11 4 7.6M8 29l-4 3.4M16 8l-1.4-5M16 32l-1.4 5"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
    </svg>
  );
}
